/**
 * Contacts surface (live) - the deep-teal sx- header band: title, a scope-naming
 * subtitle, the real header actions (scope indicator or admin My/All toggle,
 * Upload CSV, Export CSV, Add Contact), and five honest stat tiles. Total comes
 * from the endpoint's authoritative count; Hot/Warm/Cold/With Signals are
 * computed from the loaded roster and shown only once every page has loaded (a
 * dash stands in until then, never a guessed number).
 */

import type { ContactScope } from "../types";
import type { ScopeStats } from "../derive";
import { IconDownload, IconPlus, IconUpload } from "../contact-icons";

export function ContactsBand({
  total,
  stats,
  allLoaded,
  isAdmin,
  scope,
  onScopeChange,
  onUpload,
  onExport,
  onAdd,
  uploadBusy,
  exportDisabled,
}: {
  total: number | null;
  stats: ScopeStats;
  allLoaded: boolean;
  isAdmin: boolean;
  scope: ContactScope;
  onScopeChange: (scope: ContactScope) => void;
  onUpload: () => void;
  onExport: () => void;
  onAdd: () => void;
  uploadBusy: boolean;
  exportDisabled: boolean;
}): JSX.Element {
  const scopeName = scope === "all" ? "ALL CONTACTS · FIRM STORE" : "MY CONTACTS";
  const tile = (value: number, gate = true) =>
    gate && allLoaded ? value.toLocaleString() : "…";

  return (
    <header className="sx-band">
      <div className="sx-band-top">
        <div>
          <h1>Contacts</h1>
          <p className="sx-band-sub">
            {scopeName} · {total != null ? total.toLocaleString() : "…"} PEOPLE
          </p>
        </div>
        <div className="sx-band-actions">
          {isAdmin ? (
            <div className="sxc-scopetoggle" role="group" aria-label="Contact scope">
              <button
                type="button"
                className={`sxc-scopeseg${scope === "mine" ? " sxc-scopeseg--on" : ""}`}
                aria-pressed={scope === "mine"}
                onClick={() => onScopeChange("mine")}
              >
                My contacts
              </button>
              <button
                type="button"
                className={`sxc-scopeseg${scope === "all" ? " sxc-scopeseg--on" : ""}`}
                aria-pressed={scope === "all"}
                onClick={() => onScopeChange("all")}
              >
                All contacts
              </button>
            </div>
          ) : (
            <span className="sxc-scope-indicator">My contacts</span>
          )}

          <button
            type="button"
            className="sx-btn-onband"
            onClick={onUpload}
            disabled={uploadBusy}
          >
            <IconUpload /> Upload CSV
          </button>
          <button
            type="button"
            className="sx-btn-onband"
            onClick={onExport}
            disabled={exportDisabled}
          >
            <IconDownload /> Export CSV
          </button>
          <button
            type="button"
            className="sx-btn-onband sxc-btn-onband--primary"
            onClick={onAdd}
          >
            <IconPlus /> Add Contact
          </button>
        </div>
      </div>

      <div className="sx-tiles">
        <div className="sx-tile">
          <p className="sx-tile-lbl">TOTAL</p>
          <p className="sx-tile-val">
            {total != null ? total.toLocaleString() : "…"}
          </p>
        </div>
        <div className="sx-tile">
          <p className="sx-tile-lbl">HOT</p>
          <p className="sx-tile-val">{tile(stats.hot)}</p>
        </div>
        <div className="sx-tile">
          <p className="sx-tile-lbl">WARM</p>
          <p className="sx-tile-val">{tile(stats.warm)}</p>
        </div>
        <div className="sx-tile">
          <p className="sx-tile-lbl">COLD</p>
          <p className="sx-tile-val">{tile(stats.cold)}</p>
        </div>
        <div className="sx-tile">
          <p className="sx-tile-lbl">WITH SIGNALS</p>
          <p className="sx-tile-val">{tile(stats.withSignals)}</p>
        </div>
      </div>
    </header>
  );
}
